/**
 * PUBLIC universal self-check-in page (/self-checkin). Standalone (no app shell). Phase 1.3c.
 *
 * One always-on page across ALL events (decision #10). Two paths:
 *  - SCAN (ticket/voucher holders): scan or type a code → resolveScan auto-identifies the event →
 *    admission ticket = direct check-in (#11), voucher = member identity check-in (#11), a /t/:code
 *    token hands off to the token page.
 *  - WALK-IN (separate path): pick the event (today, event-local day; #12) → prove membership (3-factor)
 *    or register as a guest → a ticket is minted at the door and recorded as a walk-in.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import QrScanner from "../components/checkin/QrScanner";
import TicketLinkCheckin, {
  USLS_DISCLAIMER,
} from "../components/checkin/TicketLinkCheckin";
import MobileCheckinShell from "../components/checkin/MobileCheckinShell";
import { newAttemptId } from "../utils/selfCheckinAttemptId";
import { getTicketRewardStatus } from "../services/redemption.service";
import {
  listSelfCheckinEvents,
  resolveScan,
  checkinScan,
  walkinMember,
  walkinGuest,
  walkinSecondary,
  extractScanCode,
  buildWalkinSuccessView,
  type SelfCheckinEvent,
  type ScanResolution,
  type ScanCheckinResult,
  type WalkinResult,
  type SelfCheckinSecondary,
} from "../services/selfCheckin.service";

// Shared branded shell (cream page + dark-red U.S. LawShield header) — matches the field
// check-in / redeem surfaces so every check-in experience looks like one product.
const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <MobileCheckinShell title="Event Check-In">{children}</MobileCheckinShell>
);

const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="bg-white rounded-xl shadow p-6">{children}</div>
);

const btn =
  "w-full rounded-md bg-[#A02428] px-4 py-3 text-sm font-medium text-white hover:bg-[#8a1f22] disabled:opacity-50";
const input = "w-full rounded-md border border-gray-300 px-3 py-2 text-sm";

const STICKY_KEY = "sc_selfcheckin_event_id";
// Parallel sticky for the chosen state, so it (like the event) survives a completed
// check-in and a page reload. Restored in loadEvents only if still valid for today.
const STATE_STICKY_KEY = "sc_selfcheckin_state";

function readSticky(): string | null {
  try {
    return localStorage.getItem(STICKY_KEY);
  } catch {
    return null;
  }
}

function readStickyState(): string | null {
  try {
    return localStorage.getItem(STATE_STICKY_KEY);
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------------------------------
 * THE TICKET WALLET
 *
 * This page was built kiosk-shaped ("this runs between people at a shared kiosk"), but at real
 * events attendees check in on their OWN PHONES. Two consequences, both reported by operators:
 *
 *  1. The confirmation card had ONE ticket slot. Adding a co-member replaced the member's own
 *     ticket with the companion's, and the only way back was the "Add someone else" button —
 *     which is hidden once nobody eligible is left to add. On PROD 200 of the 210 members who
 *     have ever brought someone (95%) brought exactly ONE, so for most of them that button is
 *     gone and the only remaining control is "Done" — which wipes the ticket. The wallet holds
 *     EVERY ticket issued in this sitting and the card lets them switch between them.
 *     (Attribution lives in `invited_by_contact_id`, the superset — 220 rows;
 *     `invited_by_companion_contact_id` is only the walk-in-guest subset at 155.)
 *
 *  2. Nothing survived a reload. The member checks in, switches to their mail app to hunt for
 *     the ticket email, and the mobile browser evicts the backgrounded tab — they come back to
 *     the start screen with no ticket. The wallet is mirrored to sessionStorage so the card can
 *     be rebuilt.
 *
 * ⭐ The display is NOT time-expired, deliberately (owner 2026-09-21): the QR codes themselves
 * stop working at the end of the event, so a timer would only ever take a working ticket away
 * from someone standing in the entry line. The wallet is cleared by exactly two things — the
 * "Done" button, and the day rolling over. The day check is what keeps a SHARED KIOSK honest:
 * sessionStorage dies with the tab, "Done" clears between attendees, and a kiosk tab left open
 * overnight will not show yesterday's attendee's ticket to today's.
 * ---------------------------------------------------------------------------------------- */

const WALLET_KEY = "sc_selfcheckin_wallet";

export interface WalletTicket {
  /** Ticket code when there is one; otherwise a synthetic key. Also the React key. */
  key: string;
  /** Tab label — "You" for the person who checked themselves in, else their first name. */
  label: string;
  rewardCode: string | null;
  rewardQrUrl: string | null;
}

interface StoredWallet {
  /** Calendar date (local) the wallet was written; a wallet from another day is discarded. */
  day: string;
  /**
   * The event these tickets belong to.
   *
   * The day check alone left one hole: a SHARED KIOSK moved from one event to another on the
   * same day, then reloaded, would show the previous event's attendee their ticket until
   * someone pressed Done. Tickets are per-event, so the wallet is too — selecting a different
   * event discards it. Optional so a wallet written before this shipped still restores rather
   * than vanishing mid-event.
   */
  eventId?: string;
  tickets: WalletTicket[];
  active: number;
  title: string;
  detail?: string;
}

function today(): string {
  const d = new Date();
  // Local calendar date, not UTC — an evening event on the US west coast is already "tomorrow"
  // in UTC, which would throw the ticket away mid-event.
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

export function readWallet(): StoredWallet | null {
  try {
    const raw = sessionStorage.getItem(WALLET_KEY);
    if (!raw) {
      return null;
    }
    const w = JSON.parse(raw) as StoredWallet;
    if (!w || !Array.isArray(w.tickets) || w.tickets.length === 0) {
      return null;
    }
    if (w.day !== today()) {
      sessionStorage.removeItem(WALLET_KEY);
      return null;
    }
    return w;
  } catch {
    // Private mode, blocked site data, malformed JSON — the page must still work without it.
    return null;
  }
}

function writeWallet(w: Omit<StoredWallet, "day">): void {
  try {
    if (w.tickets.length === 0) {
      sessionStorage.removeItem(WALLET_KEY);
      return;
    }
    sessionStorage.setItem(WALLET_KEY, JSON.stringify({ ...w, day: today() }));
  } catch {
    /* non-fatal: the in-memory wallet still works for this pageview */
  }
}

function clearWallet(): void {
  try {
    sessionStorage.removeItem(WALLET_KEY);
  } catch {
    /* ignore */
  }
}

/** Normalized state key for bucketing/matching — trimmed, case-insensitive. Empty when the
 * event has no state (those events pass every state filter so they're never hidden). */
function stateKey(e: SelfCheckinEvent): string {
  return (e.location_state || "").trim().toLowerCase();
}

function cityState(e: SelfCheckinEvent): string {
  return [e.location_city, e.location_state].filter(Boolean).join(", ");
}

/** The start time shown to attendees, formatted in the event's own timezone. Hidden for all-day /
 * date-TBD events (their stored start hour is a meaningless anchor) and when there's no start_time. */
function shownTime(e: SelfCheckinEvent): string {
  if (e.all_day || e.date_tbd || !e.start_time) {
    return "";
  }
  const d = new Date(e.start_time);
  if (isNaN(d.getTime())) {
    return "";
  }
  try {
    return new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      timeZone: e.timezone || undefined,
    }).format(d);
  } catch {
    // Invalid timezone string — format without a zone rather than throw.
    return new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
    }).format(d);
  }
}

/** Rendered identity for collision detection — EXACTLY what the attendee sees (venue + city/state +
 * shown time). Never depends on a hidden field (street is not even fetched). Two rows sharing this
 * key are visually indistinguishable and must not both be selectable. */
function renderKey(e: SelfCheckinEvent): string {
  const name = (e.location_name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  return [name, cityState(e).toLowerCase(), shownTime(e)].join("|");
}

type Mode = "choose" | "scan" | "walkin";

// Exported for unit tests: the `?code=` deep-link redeem-handoff (doScanCheckin) is now only
// reached via `initialScan`, so tests seed it directly rather than through the removed scan button.
export const UniversalSelfCheckinInner: React.FC<{
  // #1a: a code opened via /self-checkin?code=<non-token> arrives here ALREADY resolved by the
  // wrapper, so the scan/check-in branch renders immediately (no re-resolve, no scanner-form flash).
  initialScan?: { code: string; resolution: ScanResolution };
}> = ({ initialScan }) => {
  const navigate = useNavigate();
  // Door-UX: the page opens STRAIGHT on the walk-in state+event picker (no intermediate
  // "how would you like to check in?" step). Scanning a printed ticket was removed as an
  // interactive entry — attendees check in on their phones — so "walkin" is the only entry.
  // The `?code=` email/SMS ticket-link path still lands here pre-resolved as `initialScan`.
  const [mode, setMode] = useState<Mode>(initialScan ? "scan" : "walkin");
  // When a scanned ticket's reward has become a redeemable voucher, we hand the holder straight to
  // /redeem — this flag suppresses the check-in confirmation card during that handoff (mirrors
  // TicketLinkCheckin's `redirecting`), avoiding a flash of the wrong screen.
  const [redirecting, setRedirecting] = useState(false);

  // ---- the ticket wallet (see WALLET_KEY above) ----
  // Seeded from sessionStorage so a reload / backgrounded-tab eviction on a personal phone
  // rebuilds the ticket instead of dumping the attendee back at the start screen.
  const restored = useRef<StoredWallet | null>(null);
  if (restored.current === null && !initialScan) {
    restored.current = readWallet();
  }
  const [wallet, setWallet] = useState<WalletTicket[]>(
    () => restored.current?.tickets ?? [],
  );
  const [activeTicket, setActiveTicket] = useState<number>(
    () => restored.current?.active ?? 0,
  );

  // ---- shared result ----
  const [result, setResult] = useState<{
    ok: boolean;
    title: string;
    detail?: string;
    // After check-in: the reward's admission QR + code, shown so the holder can photograph it for the
    // entry. rewardName is the reward's offer name (e.g. "chance at a door prize").
    rewardCode?: string | null;
    rewardQrUrl?: string | null;
    rewardName?: string | null;
    // Set when a real MEMBER was confirmed (not a guest / error) — gates the post-confirmation
    // "bringing a guest?" registration prompt below.
    isMember?: boolean;
  } | null>(() =>
    restored.current
      ? {
          ok: true,
          title: restored.current.title,
          detail: restored.current.detail,
          // isMember is deliberately NOT restored. The add-a-co-member panel runs on a signed
          // 15-minute capability that we do not persist, so offering the button after a reload
          // would show a control the server then refuses. Restoring shows the TICKETS; adding
          // someone needs a fresh check-in, which is what the existing copy already says.
          isMember: false,
        }
      : null,
  );
  const [busy, setBusy] = useState(false);
  // 1041: anonymous id for THIS person's visit (memory only), renewed by reset() and by finishForNext() after a
  // completed check-in, so the next person at a shared kiosk starts a new one. Sent with their own lookup / walk-in so the server can tell a failed member
  // lookup that then checked in from one that walked away. Identifies nobody.
  const attemptIdRef = useRef<string>(newAttemptId());
  // A visit left idle for 10 minutes is over: a kept id after a failed lookup must not link the NEXT person at a
  // shared kiosk to it (internal review M4).
  const attemptTouchedRef = useRef<number>(Date.now());
  const ATTEMPT_IDLE_MS = 10 * 60 * 1000;
  function currentAttemptId(): string {
    if (Date.now() - attemptTouchedRef.current > ATTEMPT_IDLE_MS) {
      attemptIdRef.current = newAttemptId();
    }
    attemptTouchedRef.current = Date.now();
    return attemptIdRef.current;
  }

  // ---- scan state ----
  // When arriving with a pre-resolved code (#1a), seed the resolved event straight away so the
  // check-in confirmation renders on first paint. A not-found/error resolution seeds nothing here;
  // the mount effect below surfaces the right message.
  const [scan, setScan] = useState<ScanResolution | null>(
    initialScan?.resolution?.found ? initialScan.resolution : null,
  );
  const [scanCode, setScanCode] = useState(initialScan?.code ?? "");
  const [manualCode, setManualCode] = useState("");
  const [scanError, setScanError] = useState<string | null>(null);

  // ---- walk-in state ----
  const [events, setEvents] = useState<SelfCheckinEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [filter, setFilter] = useState("");
  // Chosen state (normalized, lowercase) for the walk-in picker. Only meaningful when today's
  // events span more than one state — see the mandatory-selector logic in the render below.
  const [selectedState, setSelectedState] = useState("");
  const [eventId, setEventId] = useState("");
  const [who, setWho] = useState<"member" | "guest">("member");
  const [method, setMethod] = useState<"email" | "member">("email");
  const [email, setEmail] = useState("");
  const [memberNumber, setMemberNumber] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [zip, setZip] = useState("");
  const [phone, setPhone] = useState("");
  // Walk-in GUEST disclaimer consent (#1d). A guest is not identified as a member and hasn't seen the
  // registration-page disclaimers, so they must confirm before check-in. Members and QR/ticket-link
  // holders are never shown this (redundant — they already agreed).
  const [guestConsent, setGuestConsent] = useState(false);
  // Companion capture (seam 1): a non-member guest may name the member who brought them (email or
  // member #). Optional; used to credit the member later.
  const [invitingMember, setInvitingMember] = useState("");

  // ---- post-member-confirmation guest registration (#110) ----
  // After a MEMBER confirms, offer to register a guest they brought, right on the confirmation screen.
  // Separate state from the member walk-in fields (which stay populated on the confirmation screen) so
  // the two forms don't collide.
  const [guestStep, setGuestStep] = useState<
    "secondary" | "prompt" | "form" | "done" | null
  >(null);
  const [gFirst, setGFirst] = useState("");
  const [gLast, setGLast] = useState("");
  const [gEmail, setGEmail] = useState("");
  const [gConsent, setGConsent] = useState(false);
  const [gBusy, setGBusy] = useState(false);
  const [gError, setGError] = useState<string | null>(null);
  // Keep the guest response separate from the member response. The original flow only advanced
  // guestStep and left `result` pointing at the member, so the final card continued to show the
  // member's ticket + masked email even though the guest received a distinct ticket.
  const [registeredGuest, setRegisteredGuest] = useState<WalkinResult | null>(
    null,
  );

  // ---- post-member-confirmation: add someone on YOUR OWN membership (a "secondary") ----
  // Distinct from the guest seam above: these people are already in the CRM on the member's
  // membership account, so nothing is typed and no disclaimer is prompted (they are covered
  // members, not unidentified walk-ins). Each one gets their OWN ticket and is checked in
  // directly; the Operations tab shows "brought by <member>".
  const [secondaries, setSecondaries] = useState<SelfCheckinSecondary[]>([]);
  const [addedSecondaryIds, setAddedSecondaryIds] = useState<string[]>([]);
  const [secBusy, setSecBusy] = useState<string | null>(null); // contact_id in flight
  const [secError, setSecError] = useState<string | null>(null);
  const [lastAddWasSecondary, setLastAddWasSecondary] = useState(false);
  // The signed capability from this member's own check-in. Each add presents it instead of
  // re-sending identity fields (audit round 2, blocker 1).
  const [secondaryToken, setSecondaryToken] = useState<string | null>(null);
  // Audit round 3: the service documented that the kiosk stops offering additions after expiry,
  // but nothing honored it. The server still refuses an expired capability, so this is UX -- an
  // honest message instead of a confusing refusal.
  const [secondaryTokenExpiresAt, setSecondaryTokenExpiresAt] = useState<
    number | null
  >(null);
  // Audit round 4: expiry was only enforced when someone pressed "Add" -- the panel and its
  // buttons stayed live, so the screen claimed an action it would then refuse. A ticking clock,
  // running ONLY while the picker is on screen, lets the panel reflect reality.
  const [nowMs, setNowMs] = useState<number>(() => Date.now());

  useEffect(() => {
    if (guestStep !== "secondary" || secondaryTokenExpiresAt === null) {
      return;
    }
    setNowMs(Date.now());
    const t = setInterval(() => setNowMs(Date.now()), 10_000);
    return () => clearInterval(t);
  }, [guestStep, secondaryTokenExpiresAt]);

  const capabilityExpired =
    secondaryTokenExpiresAt !== null && nowMs >= secondaryTokenExpiresAt * 1000;

  // The person who checked THEMSELVES in owns the first slot in the wallet. Derived from
  // `result` rather than pushed at each of the nine setResult call sites, so a new check-in
  // path cannot forget to register its ticket.
  useEffect(() => {
    if (!result?.ok) {
      return;
    }
    const code = result.rewardCode ?? null;
    const qr = result.rewardQrUrl ?? null;
    if (!code && !qr) {
      return;
    }
    setWallet((prev) => {
      const key = code ?? "self";
      if (prev.some((t) => t.key === key)) {
        return prev;
      }
      const self: WalletTicket = {
        key,
        label: "You",
        rewardCode: code,
        rewardQrUrl: qr,
      };
      // The self ticket leads the list even if a restore put something there first.
      return [self, ...prev];
    });
  }, [result]);

  // Mirror the wallet so a reload can rebuild the card. Written on every change rather than at
  // the add sites, so nothing can leave the stored copy behind the rendered one.
  useEffect(() => {
    if (wallet.length === 0) {
      // ⚠️ Do NOT clear storage here. An empty in-memory wallet does not mean "the stored one
      // should go" — it is also the state at first render when arriving with a scanned code,
      // where restore is deliberately skipped. Clearing here wiped a live ticket out from
      // under someone who scanned a voucher while holding one. Every DELIBERATE clear
      // (Done, reset, switching event) calls clearWallet() itself, so this branch has nothing
      // left to do.
      return;
    }
    writeWallet({
      tickets: wallet,
      active: activeTicket,
      title: result?.title ?? "You're checked in!",
      detail: result?.detail,
      eventId: eventId || undefined,
    });
  }, [wallet, activeTicket, result?.title, result?.detail, eventId]);

  /**
   * A wallet belongs to ONE event. Drop it when the operator moves to another.
   *
   * The restore at mount cannot do this check: the event is not chosen yet at first render —
   * it arrives later, from the sticky selection or the picker. So the comparison happens here,
   * once an event is actually selected. Only a RESTORED wallet is at risk; one built in this
   * pageview is already for the event in front of us.
   */
  useEffect(() => {
    const restoredFor = restored.current?.eventId;
    if (!eventId || !restoredFor || restoredFor === eventId) {
      return;
    }
    restored.current = null;
    setWallet([]);
    setActiveTicket(0);
    clearWallet();
    setResult(null);
  }, [eventId]);

  /** Put a just-added companion's ticket in the wallet and bring it to the front of the card. */
  const addWalletTicket = useCallback(
    (label: string, code: string | null, qr: string | null) => {
      if (!code && !qr) {
        return;
      }
      setWallet((prev) => {
        const key = code ?? `added-${prev.length}`;
        const at = prev.findIndex((t) => t.key === key);
        if (at >= 0) {
          setActiveTicket(at);
          return prev;
        }
        setActiveTicket(prev.length);
        return [...prev, { key, label, rewardCode: code, rewardQrUrl: qr }];
      });
    },
    [],
  );

  // Global kill-switch is off (the whole surface is unavailable).
  const [disabled, setDisabled] = useState(false);

  const loadEvents = useCallback(async () => {
    setLoadingEvents(true);
    try {
      const { events: evs, disabled: off } = await listSelfCheckinEvents();
      setDisabled(off);
      setEvents(evs);
      // Auto-select the only event; otherwise restore a sticky selection if it's still in today's
      // list AND unambiguous (its rendered identity is unique today).
      // Restore the sticky state first (only if it's still one of today's states), so the
      // multi-state picker reopens on the operator's chosen state after a reload.
      const distinctStates = new Set(
        evs.map(stateKey).filter((s) => s.length > 0),
      );
      const savedState = readStickyState();
      if (savedState && distinctStates.has(savedState)) {
        setSelectedState((cur) => cur || savedState);
      }
      if (evs.length === 1) {
        setEventId((cur) => cur || evs[0].event_id);
      } else {
        const saved = readSticky();
        const match = saved ? evs.find((e) => e.event_id === saved) : null;
        if (
          match &&
          evs.filter((e) => renderKey(e) === renderKey(match)).length === 1
        ) {
          setEventId((cur) => cur || match.event_id);
        }
      }
    } catch {
      setEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  }, []);

  // Load once on mount (also detects the global kill-switch). "Today" is derived per-event server-side.
  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  // #1a: for a pre-resolved code (CI- admission from the legacy registration email, or a voucher),
  // the found case is already seeded into `scan` above. Here we surface the non-found outcomes with
  // the SAME truthful messaging the scan path uses (busy 429, server 5xx, kill-switch, not-found →
  // "check in as a walk-in") instead of a bare error. Token (TK-) codes never reach Inner.
  const initialCodeRan = useRef(false);
  useEffect(() => {
    if (initialCodeRan.current || !initialScan) {
      return;
    }
    initialCodeRan.current = true;
    const r = initialScan.resolution;
    if (r.disabled) {
      setDisabled(true);
    } else if (r.status === 429) {
      setScanError(
        "It's busy right now — please wait a few seconds and try again.",
      );
    } else if (r.status >= 500) {
      setScanError("Something went wrong reading that code. Please try again.");
    } else if (!r.found) {
      setScanError(
        "That code wasn't recognized. If you don't have a ticket, check in as a walk-in.",
      );
    }
  }, [initialScan]);

  // Once a scanned ticket has been checked in (fresh OR already), if its reward has been UPGRADED TO A
  // REDEEMABLE VOUCHER, hand the member straight to the redemption page — not the "checked in"
  // confirmation. This mirrors TicketLinkCheckin's gate EXACTLY: only the precise
  // `status==='redeemable'` (checked in + reward assigned + within window + unredeemed) qualifies; a
  // pending / not-found / not-assigned reward stays on the confirmation. Covers all three scan
  // sub-paths that reach this UI (tablet TK- scan, pre-resolved CI- admission, pre-resolved voucher)
  // — each carries the reward's TK- code in `result.rewardCode` only when a reward ticket exists, so
  // paths without a redeemable reward simply never redirect. `checkin_scan`'s response already returns
  // this code, so no edge change is needed.
  useEffect(() => {
    const code = result?.ok ? (result.rewardCode ?? null) : null;
    if (!code) {
      return;
    }
    let cancelled = false;
    (async () => {
      const rs = await getTicketRewardStatus(code).catch(() => null);
      if (!cancelled && rs?.found && rs.status === "redeemable") {
        setRedirecting(true);
        navigate(`/redeem?code=${encodeURIComponent(code.toUpperCase())}`, {
          replace: true,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [result, navigate]);

  function reset() {
    attemptIdRef.current = newAttemptId();
    setMode("walkin");
    setResult(null);
    setRedirecting(false);
    setScan(null);
    setScanCode("");
    setManualCode("");
    setScanError(null);
    setEventId("");
    setFilter("");
    setSelectedState("");
    setEmail("");
    setMemberNumber("");
    setFirstName("");
    setLastName("");
    setZip("");
    setPhone("");
    setGuestConsent(false);
    setInvitingMember("");
    setRegisteredGuest(null);
    setWallet([]);
    setActiveTicket(0);
    clearWallet();
  }

  // After a completed check-in: clear the person's details but KEEP the selected venue (sticky) AND
  // the chosen state so the next attendee at the same booth doesn't search anew. selectedState is
  // intentionally NOT reset here. Submit still requires an explicit tap.
  function finishForNext() {
    // 1041: a new visit id only once this visit ENDED IN A CHECK-IN. "Done" after a failed member lookup keeps the
    // same id, because the usual next step is the same person choosing "I'm not a member" -- the very outcome the
    // lookup log measures. (reset() always starts a new visit.)
    if (result?.ok) {
      attemptIdRef.current = newAttemptId();
    }
    setResult(null);
    setRedirecting(false);
    setScan(null);
    setScanCode("");
    setManualCode("");
    setScanError(null);
    setEmail("");
    setMemberNumber("");
    setFirstName("");
    setLastName("");
    setZip("");
    setPhone("");
    setGuestConsent(false);
    setInvitingMember("");
    setWho("member");
    setMethod("email");
    setMode("walkin");
    // Reset the post-confirmation guest sub-flow (#110).
    setGuestStep(null);
    setGFirst("");
    setGLast("");
    setGEmail("");
    setGConsent(false);
    setGError(null);
    setRegisteredGuest(null);
    // Reset the "add someone on my membership" sub-flow. This runs between people at a shared
    // kiosk, so leaving a previous member's household list in state would show one member's
    // family to the next person in line.
    setSecondaries([]);
    setAddedSecondaryIds([]);
    setSecBusy(null);
    setSecError(null);
    setLastAddWasSecondary(false);
    setSecondaryToken(null);
    setSecondaryTokenExpiresAt(null);
    // "Done" is the ONE deliberate act that discards the tickets. On a shared kiosk this is what
    // stops the next person in line seeing the last attendee's QR; on a phone it is the attendee
    // saying they are finished with it.
    setWallet([]);
    setActiveTicket(0);
    clearWallet();
  }

  function selectEvent(id: string) {
    setEventId(id);
    try {
      if (id) {
        localStorage.setItem(STICKY_KEY, id);
      }
    } catch {
      /* ignore storage failures */
    }
  }

  function changeVenue() {
    setEventId("");
    setFilter("");
    try {
      localStorage.removeItem(STICKY_KEY);
    } catch {
      /* ignore */
    }
  }

  function selectState(s: string) {
    setSelectedState(s);
    setFilter("");
    try {
      if (s) {
        localStorage.setItem(STATE_STICKY_KEY, s);
      }
    } catch {
      /* ignore storage failures */
    }
  }

  // Return to the state chooser (multi-state picker). Also drops any half-made event selection
  // and its sticky, since that event belonged to the previous state.
  function clearState() {
    setSelectedState("");
    setEventId("");
    setFilter("");
    try {
      localStorage.removeItem(STATE_STICKY_KEY);
      localStorage.removeItem(STICKY_KEY);
    } catch {
      /* ignore */
    }
  }

  // ---- scan handlers ----
  async function handleCode(raw: string) {
    const code = extractScanCode(raw);
    if (!code) {
      return;
    }
    setScanCode(code);
    setScanError(null);
    setBusy(true);
    try {
      const r = await resolveScan(code);
      if (r.disabled) {
        setDisabled(true);
        return;
      }
      // BAM! 2026-07-07: a rate-limit (429) or server error (5xx) response has no `found`
      // flag — it must NOT be shown as "code wasn't recognized" (guests concluded their
      // valid tickets were bad and gave up). Tell them the truthful next action instead.
      if (r.status === 429) {
        setScanError(
          "It's busy right now — please wait a few seconds and try again.",
        );
        return;
      }
      if (r.status >= 500) {
        setScanError(
          "Something went wrong reading that code. Please try again.",
        );
        return;
      }
      // A ticket (TK-) resolves as a multi-behavior "token". This tablet is the turnstile: rather than
      // handing off to the personal /t/ page (which can't check in), we admit it here via doScanCheckin.
      if (!r.found) {
        // Keep `scan` null so the input + error stays shown (a truthy scan would switch to the
        // resolved/check-in branch). Caught by the render gate.
        setScanError(
          "That code wasn't recognized. If you don't have a ticket, check in as a walk-in.",
        );
        return;
      }
      setScan(r);
    } catch {
      setScanError("Something went wrong reading that code. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function doScanCheckin() {
    if (!scanCode || busy) {
      return;
    }
    setBusy(true);
    try {
      const res: ScanCheckinResult = await checkinScan(
        scanCode,
        currentAttemptId(),
      );
      if (res.status === 200 && res.checkedIn) {
        setResult({
          ok: true,
          title: res.alreadyCheckedIn
            ? "Already checked in"
            : "You're checked in!",
          detail:
            [
              // 2026-09-08 owner rule: check-in mints a TICKET. The operator voucher upgrade is the
              // ONLY thing that turns it into a voucher, so nothing here names or values a reward —
              // doing so promises the member something they may never be granted. (The offer names
              // read "<venue> — Ticket" anyway, so "carries your ..." was never good copy.)
              // §HIGH5: honest, SEPARATE email states — only a CONFIRMED acceptance says "sent".
              res.emailAccepted && res.maskedEmail
                ? `A reward was sent to ${res.maskedEmail}.`
                : res.emailAmbiguous
                  ? "We're confirming your reward email — please check your inbox shortly."
                  : res.emailSkippedNoEmail
                    ? "We don't have an email on file — use the code above."
                    : null,
            ]
              .filter(Boolean)
              .join(" ") || undefined,
          rewardCode: res.rewardTicketCode ?? null,
          rewardQrUrl: res.rewardTicketQrUrl ?? null,
          rewardName: res.reward_offer_name ?? null,
        });
      } else if (res.alreadyCheckedIn) {
        setResult({
          ok: true,
          title: "Already checked in",
          detail: res.attendeeName
            ? `Welcome back, ${res.attendeeName}.`
            : undefined,
        });
      } else {
        setResult({
          ok: false,
          title: "Couldn't check you in",
          detail: res.error ?? undefined,
        });
      }
    } catch {
      setResult({
        ok: false,
        title: "Network error",
        detail: "Please try again.",
      });
    } finally {
      setBusy(false);
    }
  }

  // ---- walk-in submit ----
  async function submitWalkin() {
    if (!eventId || busy) {
      return;
    }
    setBusy(true);
    try {
      let res: WalkinResult;
      if (who === "member") {
        res = await walkinMember({
          eventId,
          email: method === "email" ? email.trim() : undefined,
          memberNumber: method === "member" ? memberNumber.trim() : undefined,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          zip: zip.trim(),
          attemptId: currentAttemptId(),
        });
      } else {
        const inv = invitingMember.trim();
        res = await walkinGuest({
          eventId,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim() || undefined,
          phone: phone.trim() || undefined,
          zip: zip.trim() || undefined,
          disclaimerAck: guestConsent,
          invitingMemberEmail: inv.includes("@") ? inv : undefined,
          invitingMemberNumber: inv && !inv.includes("@") ? inv : undefined,
          attemptId: currentAttemptId(),
        });
      }
      if (res.status === 200 && res.checkedIn) {
        // The minted TK- ticket's code + QR are shown whenever the ticket exists — the OPTIONAL reward name
        // (which the backend returns null for on a transient lookup failure) no longer gates them (audit
        // MED#1). Pure builder → unit-tested in selfCheckin.service.test.ts.
        setResult({
          ok: true,
          title: "You're checked in!",
          ...buildWalkinSuccessView(res),
          isMember: res.isMember,
        });
        // A confirmed MEMBER may have brought a guest — offer to register them (#110). If they
        // have other people on their own membership, offer those FIRST: it is one tap with no
        // typing, and it is the more common case at the door than an outside guest.
        if (res.isMember && who === "member") {
          const co = (res.secondaries ?? []).filter(
            (r) => !r.already_checked_in,
          );
          setSecondaries(res.secondaries ?? []);
          setSecondaryToken(res.secondaryToken ?? null);
          setSecondaryTokenExpiresAt(res.secondaryTokenExpiresAt ?? null);
          // Without a capability there is no way to add anyone, so don't offer the step.
          setGuestStep(
            co.length > 0 && res.secondaryToken ? "secondary" : "prompt",
          );
        }
      } else if (res.status === 401) {
        setResult({
          ok: false,
          title: "We couldn't confirm your membership",
          detail: res.error,
        });
      } else {
        setResult({
          ok: false,
          title: "Couldn't check you in",
          detail: res.error,
        });
      }
    } catch {
      setResult({
        ok: false,
        title: "Network error",
        detail: "Please try again.",
      });
    } finally {
      setBusy(false);
    }
  }

  // Add one person from the confirmed member's OWN membership account. Their identity is already
  // on file, so the only input is which person — the server re-confirms the member and re-derives
  // the eligible list, and refuses any contact that is not on their account.
  async function submitSecondary(contactId: string) {
    if (!eventId || secBusy) {
      return;
    }
    // Re-derive at click time rather than trusting the 10s tick, so a click landing just after
    // expiry is still refused locally.
    const expired =
      secondaryTokenExpiresAt !== null &&
      Date.now() >= secondaryTokenExpiresAt * 1000;
    if (!secondaryToken || expired) {
      setSecError(
        "Please check yourself in again before adding someone on your membership.",
      );
      if (expired) {
        setSecondaryToken(null);
      }
      return;
    }
    setSecBusy(contactId);
    setSecError(null);
    try {
      const res = await walkinSecondary({
        eventId,
        secondaryToken,
        secondaryContactId: contactId,
      });
      if (res.status === 200 && res.checkedIn) {
        setAddedSecondaryIds((prev) => [...prev, contactId]);
        setRegisteredGuest(res);
        setLastAddWasSecondary(true);
        setGuestStep("done");
        // Their ticket JOINS the member's rather than replacing it on screen.
        addWalletTicket(
          secondaries
            .find((r) => r.contact_id === contactId)
            ?.first_name?.trim() || "Added",
          res.rewardTicketCode ?? null,
          res.rewardTicketQrUrl ?? null,
        );
      } else {
        setSecError(
          res.error ||
            "We couldn't add them. Please see a USLS representative.",
        );
      }
    } catch {
      setSecError("Network error. Please try again.");
    } finally {
      setSecBusy(null);
    }
  }

  // Register the guest a just-confirmed member brought, via the SAME public guest walk-in path
  // (walkinGuest). Credits the guest to the confirmed member (companion seam) using their identity.
  async function submitGuest() {
    if (!eventId || gBusy) {
      return;
    }
    if (!gFirst.trim() || !gLast.trim() || !gEmail.trim() || !gConsent) {
      return;
    }
    setGBusy(true);
    setGError(null);
    try {
      const res = await walkinGuest({
        eventId,
        firstName: gFirst.trim(),
        lastName: gLast.trim(),
        email: gEmail.trim(),
        disclaimerAck: gConsent,
        invitingMemberEmail:
          method === "email" ? email.trim() || undefined : undefined,
        invitingMemberNumber:
          method === "member" ? memberNumber.trim() || undefined : undefined,
      });
      if (res.status === 200 && res.checkedIn) {
        setRegisteredGuest(res);
        setLastAddWasSecondary(false);
        setGuestStep("done");
        addWalletTicket(
          gFirst.trim() || "Guest",
          res.rewardTicketCode ?? null,
          res.rewardTicketQrUrl ?? null,
        );
      } else {
        setGError(
          res.error || "We couldn't register your guest. Please try again.",
        );
      }
    } catch {
      setGError("Network error. Please try again.");
    } finally {
      setGBusy(false);
    }
  }

  // ---- render ----
  // Redeemable-reward tickets are being handed off to /redeem — show a brief transition rather than
  // flashing the check-in confirmation (mirrors TicketLinkCheckin).
  if (redirecting) {
    return (
      <Shell>
        <Card>
          <p className="py-8 text-center text-sm text-gray-600">
            Taking you to your reward…
          </p>
        </Card>
      </Shell>
    );
  }
  if (result) {
    const showingGuest = guestStep === "done" && registeredGuest !== null;
    const guestView = showingGuest
      ? buildWalkinSuccessView(registeredGuest)
      : null;
    // The same confirmation card serves an outside GUEST and a co-member added from the member's
    // own membership, so name the person correctly rather than calling a spouse "your guest".
    const lastAddedId = addedSecondaryIds[addedSecondaryIds.length - 1] ?? null;
    const lastAddedFirst =
      lastAddWasSecondary && lastAddedId
        ? secondaries
            .find((r) => r.contact_id === lastAddedId)
            ?.first_name?.trim() || null
        : null;
    const addedTitle = lastAddWasSecondary
      ? `${lastAddedFirst ?? "They"} ${lastAddedFirst ? "is" : "are"} checked in!`
      : "Your guest is checked in!";
    const addedNoun = lastAddWasSecondary ? "them" : "the guest";
    const addedPossessive = lastAddWasSecondary ? "their" : "the guest's";
    const addedSubject = lastAddWasSecondary ? "They are" : "The guest is";
    const remainingSecondaries = secondaries.filter(
      (r) => !r.already_checked_in && !addedSecondaryIds.includes(r.contact_id),
    );
    const displayed = showingGuest
      ? {
          ok: true,
          title: addedTitle,
          detail: registeredGuest.rewardEmailed
            ? guestView?.detail || `We emailed ${addedNoun} their ticket.`
            : registeredGuest.rewardTicketCode
              ? `We couldn't email ${addedPossessive} ticket. Keep this screen open or have them photograph the code below.`
              : `${addedSubject} checked in, but no ticket was issued or emailed. Please see a U.S. LawShield representative.`,
          rewardCode: guestView?.rewardCode ?? null,
          rewardQrUrl: guestView?.rewardQrUrl ?? null,
          rewardName: guestView?.rewardName ?? null,
        }
      : result;
    // The ticket ON SCREEN comes from the wallet, so every ticket issued in this sitting stays
    // reachable. `displayed` still supplies the heading and the wording for the LAST action.
    const activeIdx =
      wallet.length === 0 ? -1 : Math.min(activeTicket, wallet.length - 1);
    const shown = activeIdx >= 0 ? wallet[activeIdx] : null;
    const panelCode = shown ? shown.rewardCode : (displayed.rewardCode ?? null);
    const panelQr = shown ? shown.rewardQrUrl : (displayed.rewardQrUrl ?? null);
    // ⭐ When the person we just added got NO ticket, show NOTHING rather than the member's.
    // The wallet would otherwise fall back to the member's ticket under a heading about the
    // companion, and staff would scan the member's QR for them. Pre-dates the wallet and is
    // guarded by UniversalSelfCheckin.guestFlow.test.tsx — the confirmation text already tells
    // them to see a representative.
    const addedPersonHasNoTicket =
      showingGuest && !displayed.rewardCode && !displayed.rewardQrUrl;

    return (
      <Shell>
        <Card>
          <div
            className={`mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full ${
              displayed.ok ? "bg-green-100" : "bg-amber-100"
            }`}
          >
            <span
              className={`text-xl ${displayed.ok ? "text-green-600" : "text-amber-600"}`}
            >
              {displayed.ok ? "✓" : "!"}
            </span>
          </div>
          <h2 className="text-center text-lg font-semibold text-gray-900 mb-1">
            {displayed.title}
          </h2>
          {displayed.detail && (
            <p className="text-center text-sm text-gray-600 mb-4">
              {displayed.detail}
            </p>
          )}
          {displayed.ok &&
            !addedPersonHasNoTicket &&
            (panelQr || panelCode) && (
              /*
               * The door ticket. Members were being checked in and then sent hunting through their
               * email for the voucher at the entry line, so this panel has to read as the ticket
               * itself: a solid green band carrying the one instruction that matters, above the code.
               * The instruction LEADS the panel — as a grey footnote under the QR it was being
               * missed, which is what sent people back to their inbox.
               */
              <div className="mb-4 overflow-hidden rounded-xl border-2 border-green-600 bg-white shadow-sm">
                <div className="bg-green-600 px-4 py-3 text-center">
                  <p className="text-sm font-extrabold uppercase tracking-wide text-white">
                    Keep this screen open to show at entry
                  </p>
                </div>
                {/*
                 * More than one person checked in on this phone, so BOTH tickets have to stay
                 * reachable. Previously the companion's ticket replaced the member's and the only
                 * way back was a button that disappears once there is nobody left to add, which is
                 * why members arrived at the entry line with no ticket.
                 */}
                {wallet.length > 1 && (
                  <div
                    className="flex gap-1 border-b border-green-200 bg-green-100 p-1"
                    role="tablist"
                    aria-label="Tickets checked in on this phone"
                  >
                    {wallet.map((t, i) => (
                      <button
                        key={t.key}
                        type="button"
                        role="tab"
                        aria-selected={i === activeIdx}
                        onClick={() => setActiveTicket(i)}
                        className={`flex-1 truncate rounded-md px-2 py-2 text-sm font-semibold ${
                          i === activeIdx
                            ? "bg-white text-green-800 shadow-sm ring-1 ring-green-300"
                            : "text-green-900 hover:bg-green-50"
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                )}
                <div className="bg-green-50 px-4 py-4 text-center">
                  {wallet.length > 1 && shown && (
                    <p className="text-sm font-semibold text-green-900">
                      {shown.label === "You"
                        ? "Your ticket"
                        : `${shown.label}'s ticket`}
                    </p>
                  )}
                  {panelQr && (
                    <img
                      src={panelQr}
                      alt={
                        shown && shown.label !== "You"
                          ? `QR code for ${shown.label}`
                          : "Your reward QR code"
                      }
                      className="mx-auto my-3 h-44 w-44 rounded-lg bg-white p-2 ring-1 ring-green-300"
                    />
                  )}
                  {panelCode && (
                    <p className="text-xl font-bold tracking-wider text-gray-900">
                      {panelCode}
                    </p>
                  )}
                  <p className="mt-2 text-xs font-medium text-green-800">
                    {wallet.length > 1
                      ? "Staff will scan each code. Switch above to show the other."
                      : "Staff will scan this code."}
                  </p>
                </div>
              </div>
            )}
          {result.ok && result.isMember && guestStep === "secondary" ? (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-4">
              <p className="text-center text-sm font-medium text-gray-900">
                Anyone else on your membership here with you?
              </p>
              <p className="mt-1 text-center text-xs text-gray-600">
                We&apos;ll check them in and email them their own ticket.
              </p>
              <div className="mt-3 space-y-2">
                {secondaries.map((r) => {
                  const nm =
                    [r.first_name, r.last_name].filter(Boolean).join(" ") ||
                    "Member on your account";
                  const added = addedSecondaryIds.includes(r.contact_id);
                  const done = added || r.already_checked_in;
                  return (
                    <div
                      key={r.contact_id}
                      className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-white px-3 py-2"
                    >
                      <div className="min-w-0 text-left">
                        <p className="truncate text-sm font-medium text-gray-900">
                          {nm}
                        </p>
                        <p className="truncate text-xs text-gray-500">
                          {done
                            ? "Already checked in"
                            : r.has_email
                              ? (r.masked_email ?? "")
                              : "No email on file — see a representative"}
                        </p>
                      </div>
                      <button
                        type="button"
                        disabled={
                          done ||
                          !r.has_email ||
                          secBusy !== null ||
                          capabilityExpired
                        }
                        className="shrink-0 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                        onClick={() => submitSecondary(r.contact_id)}
                      >
                        {secBusy === r.contact_id
                          ? "Adding…"
                          : done
                            ? "Added"
                            : "Add"}
                      </button>
                    </div>
                  );
                })}
              </div>
              {capabilityExpired ? (
                <p className="mt-2 text-center text-xs text-amber-700">
                  This has expired. Please check yourself in again to add
                  someone on your membership.
                </p>
              ) : null}
              {secError ? (
                <p className="mt-2 text-center text-xs text-red-600">
                  {secError}
                </p>
              ) : null}
              <button
                type="button"
                className="mt-3 w-full rounded-md border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-800 hover:bg-gray-50"
                onClick={() => {
                  setSecError(null);
                  setGuestStep("prompt");
                }}
              >
                {addedSecondaryIds.length > 0 ? "Continue" : "No, just me"}
              </button>
            </div>
          ) : result.ok && result.isMember && guestStep === "prompt" ? (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-4">
              <p className="text-center text-sm font-medium text-gray-900">
                Bringing a guest? Let&apos;s get them registered too.
              </p>
              <div className="mt-3 flex gap-3">
                <button
                  type="button"
                  className="w-full rounded-md border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-800 hover:bg-gray-50"
                  onClick={finishForNext}
                >
                  No, just me
                </button>
                <button
                  type="button"
                  className={btn}
                  onClick={() => setGuestStep("form")}
                >
                  Yes
                </button>
              </div>
            </div>
          ) : result.ok && result.isMember && guestStep === "form" ? (
            <div className="space-y-3 text-left">
              <p className="text-center text-sm font-medium text-gray-900">
                Register your guest
              </p>
              <input
                type="text"
                className={input}
                placeholder="First name"
                value={gFirst}
                onChange={(e) => setGFirst(e.target.value)}
                autoComplete="off"
              />
              <input
                type="text"
                className={input}
                placeholder="Last name"
                value={gLast}
                onChange={(e) => setGLast(e.target.value)}
                autoComplete="off"
              />
              <input
                type="email"
                className={input}
                placeholder="Email (required — we'll email their ticket)"
                value={gEmail}
                onChange={(e) => setGEmail(e.target.value)}
                autoComplete="off"
              />
              <label className="flex items-start gap-2 text-xs text-gray-600">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4"
                  checked={gConsent}
                  onChange={(e) => setGConsent(e.target.checked)}
                />
                <span>{USLS_DISCLAIMER}</span>
              </label>
              {gError && (
                <p className="text-center text-sm text-red-600">{gError}</p>
              )}
              <button
                type="button"
                className={btn}
                disabled={
                  !gFirst.trim() ||
                  !gLast.trim() ||
                  !gEmail.trim() ||
                  !gConsent ||
                  gBusy
                }
                onClick={submitGuest}
              >
                {gBusy ? "Registering…" : "Register guest"}
              </button>
              <button
                type="button"
                className="w-full text-center text-sm text-gray-500 underline"
                onClick={() => {
                  setGuestStep("prompt");
                  setGError(null);
                }}
              >
                Back
              </button>
            </div>
          ) : result.ok && result.isMember && guestStep === "done" ? (
            <>
              <p className="mb-4 text-center text-sm text-gray-700">
                {lastAddWasSecondary
                  ? "Thanks! They're checked in."
                  : "Thanks! Your guest is registered."}
              </p>
              {remainingSecondaries.length > 0 ? (
                <button
                  type="button"
                  className="mb-3 w-full rounded-md border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-800 hover:bg-gray-50"
                  onClick={() => {
                    setRegisteredGuest(null);
                    setSecError(null);
                    setGuestStep("secondary");
                  }}
                >
                  Add someone else on my membership
                </button>
              ) : null}
              <button type="button" className={btn} onClick={finishForNext}>
                Done
              </button>
            </>
          ) : (
            <button type="button" className={btn} onClick={finishForNext}>
              Done
            </button>
          )}
        </Card>
      </Shell>
    );
  }

  if (disabled) {
    return (
      <Shell>
        <Card>
          <h2 className="text-center text-lg font-semibold text-gray-900 mb-1">
            Self check-in isn&apos;t available
          </h2>
          <p className="text-center text-sm text-gray-600">
            Self check-in isn&apos;t available right now. Please see a USLS
            representative for assistance.
          </p>
        </Card>
      </Shell>
    );
  }

  if (mode === "scan") {
    return (
      <Shell>
        <Card>
          {!scan ? (
            <>
              <p className="text-sm text-gray-600 mb-3 text-center">
                Scan your QR code, or enter the code below.
              </p>
              <QrScanner
                onDecode={handleCode}
                onError={(m) => setScanError(m)}
                buttonLabel="Scan QR code"
              />
              <div className="my-3 text-center text-xs text-gray-400">
                — or —
              </div>
              <div className="flex gap-2">
                <input
                  className={input}
                  placeholder="Enter code"
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value)}
                />
                <button
                  type="button"
                  className="shrink-0 rounded-md bg-blue-600 px-4 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  disabled={busy || !manualCode.trim()}
                  onClick={() => void handleCode(manualCode)}
                >
                  Go
                </button>
              </div>
              {scanError && (
                <p className="mt-3 text-sm text-amber-700">{scanError}</p>
              )}
            </>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-1">Event</p>
              <p className="font-medium text-gray-900 mb-4">
                {scan.eventTitle ?? "Your event"}
              </p>
              {scan.alreadyCheckedIn ? (
                <p className="mb-4 rounded-md bg-green-50 px-4 py-3 text-sm text-green-800">
                  You're already checked in.
                </p>
              ) : null}
              <button
                type="button"
                className={btn}
                disabled={busy}
                onClick={() => void doScanCheckin()}
              >
                {busy ? "Checking in…" : "Check me in"}
              </button>
            </>
          )}
          <button
            type="button"
            className="mt-4 w-full text-sm text-gray-500 underline"
            onClick={reset}
          >
            Back
          </button>
        </Card>
      </Shell>
    );
  }

  // mode === "walkin"
  const selected = events.find((e) => e.event_id === eventId) || null;
  const keyCounts = new Map<string, number>();
  for (const e of events) {
    const k = renderKey(e);
    keyCounts.set(k, (keyCounts.get(k) || 0) + 1);
  }
  // Distinct non-empty states among today's events, as [normalizedKey, displayLabel]. When there's
  // more than one, the attendee must pick their state before any event list appears.
  const distinctStates = Array.from(
    new Map(
      events
        .filter((e) => stateKey(e).length > 0)
        .map(
          (e) =>
            [stateKey(e), (e.location_state || "").trim()] as [string, string],
        ),
    ).entries(),
  ).sort((a, b) => a[1].localeCompare(b[1]));
  const multiState = distinctStates.length > 1;
  const needStateChoice = multiState && !selectedState;
  // Event list scoped to the chosen state (events with no state pass every filter so they're never
  // hidden), then the existing free-text venue/city filter.
  const stateScoped =
    multiState && selectedState
      ? events.filter(
          (e) => stateKey(e) === selectedState || stateKey(e) === "",
        )
      : events;
  const filtered = stateScoped.filter((e) => {
    const hay =
      `${e.title || ""} ${e.location_name || ""} ${cityState(e)}`.toLowerCase();
    return hay.includes(filter.trim().toLowerCase());
  });
  const memberReady =
    (method === "email" ? email.trim() : memberNumber.trim()) &&
    firstName.trim() &&
    lastName.trim() &&
    zip.trim();
  // Email is REQUIRED for a walk-in guest so we can email the QR ticket (phone stays optional).
  // Guests must also confirm the USLS disclaimers (#1d) — they haven't seen them elsewhere.
  const guestReady =
    firstName.trim() && lastName.trim() && email.trim() && guestConsent;
  const canSubmit = !!eventId && (who === "member" ? memberReady : guestReady);

  return (
    <Shell>
      <Card>
        <h1 className="mb-4 whitespace-nowrap text-center text-lg font-bold text-gray-900 sm:text-xl">
          Find Your Event and Check In
        </h1>
        {!selected ? (
          <>
            <label className="block text-xs font-medium text-gray-500 mb-2">
              Find your event
            </label>
            {loadingEvents ? (
              <p className="text-sm text-gray-500 py-2">Loading events…</p>
            ) : events.length === 0 ? (
              <p className="text-sm text-gray-600 py-2">
                No events are available for check-in here right now. Please see
                a USLS representative.
              </p>
            ) : needStateChoice ? (
              <>
                <p className="text-sm text-gray-600 mb-3">
                  Events are running in more than one state today. Select your
                  state to see its events.
                </p>
                <div className="space-y-2">
                  {distinctStates.map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => selectState(key)}
                      className="w-full rounded-md border border-gray-300 px-4 py-3 text-left font-medium text-gray-900 hover:border-blue-500 hover:bg-blue-50"
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                {multiState && selectedState && (
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-sm text-gray-600">
                      State:{" "}
                      <span className="font-medium text-gray-900">
                        {distinctStates.find(
                          ([k]) => k === selectedState,
                        )?.[1] || selectedState.toUpperCase()}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={clearState}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Change state
                    </button>
                  </div>
                )}
                {stateScoped.length > 3 && (
                  <input
                    className={`${input} mb-3`}
                    placeholder="Search by venue or city"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                  />
                )}
                <div className="space-y-2">
                  {filtered.map((e) => {
                    const ambiguous = (keyCounts.get(renderKey(e)) || 0) > 1;
                    // Lead with the EVENT TITLE so a tester/operator can tell apart two events at the
                    // same venue (EIB feedback 2026-08-14); keep the venue in the meta line.
                    const meta = [
                      e.title ? e.location_name : null,
                      cityState(e),
                      shownTime(e),
                    ]
                      .filter(Boolean)
                      .join(" · ");
                    return (
                      <button
                        key={e.event_id}
                        type="button"
                        disabled={ambiguous}
                        onClick={() => selectEvent(e.event_id)}
                        className={`w-full rounded-md border px-4 py-3 text-left ${
                          ambiguous
                            ? "cursor-not-allowed border-gray-200 bg-gray-50"
                            : "border-gray-300 hover:border-blue-500 hover:bg-blue-50"
                        }`}
                      >
                        <div className="font-medium text-gray-900">
                          {e.title || e.location_name}
                        </div>
                        {meta && (
                          <div className="text-sm text-gray-500">{meta}</div>
                        )}
                        {ambiguous && (
                          <div className="mt-1 text-xs text-amber-700">
                            More than one event at this venue — please see a
                            USLS representative.
                          </div>
                        )}
                      </button>
                    );
                  })}
                  {filtered.length === 0 && (
                    <p className="text-sm text-gray-500 py-2">
                      No matching events.
                    </p>
                  )}
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <div className="mb-4 rounded-md bg-blue-50 px-4 py-3">
              <p className="mb-0.5 text-xs font-medium text-blue-700">
                Checking in at
              </p>
              <p className="font-semibold text-gray-900">
                {selected.title || selected.location_name}
              </p>
              {[
                selected.title ? selected.location_name : null,
                cityState(selected),
                shownTime(selected),
              ].filter(Boolean).length > 0 && (
                <p className="text-sm text-gray-600">
                  {[
                    selected.title ? selected.location_name : null,
                    cityState(selected),
                    shownTime(selected),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              )}
              {events.length > 1 && (
                <button
                  type="button"
                  className="mt-2 text-sm text-blue-700 underline"
                  onClick={changeVenue}
                >
                  Change venue
                </button>
              )}
            </div>
            <div className="flex rounded-md bg-gray-100 p-1 text-sm mb-3">
              <button
                type="button"
                onClick={() => setWho("member")}
                className={`flex-1 rounded py-1.5 font-medium ${who === "member" ? "bg-white shadow text-gray-900" : "text-gray-500"}`}
              >
                I'm a member
              </button>
              <button
                type="button"
                onClick={() => setWho("guest")}
                className={`flex-1 rounded py-1.5 font-medium ${who === "guest" ? "bg-white shadow text-gray-900" : "text-gray-500"}`}
              >
                I'm not a member
              </button>
            </div>

            {who === "member" ? (
              <div className="space-y-3">
                <div className="flex rounded-md bg-gray-100 p-1 text-sm">
                  <button
                    type="button"
                    onClick={() => setMethod("email")}
                    className={`flex-1 rounded py-1.5 font-medium ${method === "email" ? "bg-white shadow text-gray-900" : "text-gray-500"}`}
                  >
                    Email
                  </button>
                  <button
                    type="button"
                    onClick={() => setMethod("member")}
                    className={`flex-1 rounded py-1.5 font-medium ${method === "member" ? "bg-white shadow text-gray-900" : "text-gray-500"}`}
                  >
                    Member #
                  </button>
                </div>
                {method === "email" ? (
                  <input
                    type="email"
                    className={input}
                    placeholder="Email on your membership"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                ) : (
                  <input
                    type="text"
                    className={input}
                    placeholder="Member number"
                    value={memberNumber}
                    onChange={(e) => setMemberNumber(e.target.value)}
                  />
                )}
                <input
                  type="text"
                  className={input}
                  placeholder="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
                <input
                  type="text"
                  className={input}
                  placeholder="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
                {/* Door-UX #1: member-identity ZIP is the ZIP on the MEMBER'S account (home ZIP), not
                    the event/venue ZIP. Label/placeholder + helper only — validation is unchanged. */}
                <input
                  type="text"
                  inputMode="numeric"
                  className={input}
                  placeholder="Member's home ZIP"
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                />
                <p className="-mt-1 text-xs text-gray-500">
                  The ZIP code on the member's account.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <input
                  type="text"
                  className={input}
                  placeholder="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
                <input
                  type="text"
                  className={input}
                  placeholder="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
                <input
                  type="email"
                  className={input}
                  placeholder="Email (required — we'll email your ticket)"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <input
                  type="tel"
                  className={input}
                  placeholder="Phone (optional)"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
                <input
                  type="text"
                  inputMode="numeric"
                  className={input}
                  placeholder="ZIP code (optional)"
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                />
                {/* Companion capture (seam 1): a guest brought by a member can name them so the member
                    gets credit. Optional; matched to an active member on the server. */}
                <input
                  type="text"
                  className={input}
                  placeholder="Here with a member? Their email or member # (optional)"
                  value={invitingMember}
                  onChange={(e) => setInvitingMember(e.target.value)}
                />
                {/* Guests confirm the USLS disclaimers here (#1d) — they haven't seen them on a
                    registration page, and they aren't an identified member. */}
                <label className="flex items-start gap-2 text-left text-xs text-gray-600">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 flex-shrink-0"
                    checked={guestConsent}
                    onChange={(e) => setGuestConsent(e.target.checked)}
                  />
                  <span>{USLS_DISCLAIMER}</span>
                </label>
              </div>
            )}

            <button
              type="button"
              className={`${btn} mt-4`}
              disabled={!canSubmit || busy}
              onClick={() => void submitWalkin()}
            >
              {busy ? "Checking in…" : "Check me in"}
            </button>
          </>
        )}

        <button
          type="button"
          className="mt-4 w-full text-sm text-gray-500 underline"
          onClick={reset}
        >
          Back
        </button>
      </Card>
    </Shell>
  );
};

// When the URL carries ?code=<X>, resolve what kind of code it is and route accordingly (#1a):
//  - a redemption_token (TK-, the ticket-email one-click link) → the subject-bound ticket check-in
//    flow (member auto-checkin, reward panel).
//  - ANY other code — a CI- admission code (legacy registration-confirmation email QR), a voucher,
//    or an unrecognized string → the universal scan/walk-in page with the code pre-loaded, so it
//    resolves + checks in exactly as a scan would.
// This makes /self-checkin?code= work for EVERY code shape, so all check-in emails can point here.
const SelfCheckinCodeRouter: React.FC<{ code: string }> = ({ code }) => {
  const [state, setState] = useState<
    | { phase: "loading" }
    | { phase: "token" }
    | { phase: "other"; resolution: ScanResolution }
  >({ phase: "loading" });
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) {
      return;
    }
    ran.current = true;
    (async () => {
      try {
        const r = await resolveScan(code);
        // Only a genuine redemption_token keeps the subject-bound one-click ticket flow. Everything
        // else (admission, voucher) — and a 429/5xx/not-found resolution — is handed to the scan page
        // pre-resolved, which shows the truthful next step (check-in, or "not recognized → walk in").
        if (r.found && r.kind === "token") {
          setState({ phase: "token" });
        } else {
          setState({ phase: "other", resolution: r });
        }
      } catch {
        // Network failure resolving the code — send them to the scan page seeded as "not found" so
        // they can retry or fall back to walk-in, rather than a dead end.
        setState({
          phase: "other",
          resolution: { status: 0, found: false },
        });
      }
    })();
  }, []);

  if (state.phase === "loading") {
    return (
      <Shell>
        <Card>
          <p className="py-8 text-center text-gray-500">
            Looking up your code…
          </p>
        </Card>
      </Shell>
    );
  }
  if (state.phase === "token") {
    return <TicketLinkCheckin code={code} />;
  }
  return (
    <UniversalSelfCheckinInner
      initialScan={{ code, resolution: state.resolution }}
    />
  );
};

const UniversalSelfCheckin: React.FC = () => {
  let urlCode = "";
  try {
    urlCode =
      new URLSearchParams(window.location.search).get("code")?.trim() || "";
  } catch {
    urlCode = "";
  }
  if (!urlCode) {
    return <UniversalSelfCheckinInner />;
  }
  // Ticket tokens are always `TK-XXXXXXXX` (generate_token_code); route them straight to the
  // subject-bound one-click flow with NO extra round-trip — this is the high-volume ticket-email
  // path. Any other code (CI- admission from the legacy registration email, voucher code, or an
  // unrecognized string) is resolved by the router and sent to the right check-in path (#1a).
  if (/^TK-/i.test(urlCode)) {
    return <TicketLinkCheckin code={urlCode} />;
  }
  return <SelfCheckinCodeRouter code={urlCode} />;
};

export default UniversalSelfCheckin;
